import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownToLine, ArrowUpFromLine, Plus } from "lucide-react";
import { listStock, type StockItem } from "@/api/stock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/Panel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StockLevelEditor } from "./StockLevelEditor";
import { MovementHistory } from "./MovementHistory";
import { LocationOverview } from "./LocationOverview";

function fmtEUR(n: string | number | null | undefined): string {
  if (n === null || n === undefined || n === "") return "—";
  const num = typeof n === "number" ? n : Number(n);
  if (Number.isNaN(num)) return "—";
  return num.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

interface RiskBucket {
  kind: "ok" | "low" | "crit" | "draft";
  label: string;
}

function classifyStock(item: StockItem): RiskBucket {
  if (!item.is_active) return { kind: "draft", label: "Entwurf" };
  if (item.quantity_total === 0) return { kind: "crit", label: "Kritisch" };
  if (item.quantity_alert && item.quantity_total <= item.quantity_alert) {
    return { kind: "low", label: "Niedrig" };
  }
  return { kind: "ok", label: "Aktiv" };
}

const STATUS_PILL: Record<RiskBucket["kind"], string> = {
  ok: "bg-success-soft text-success",
  low: "bg-warn-soft text-warn",
  crit: "bg-coral-soft text-coral",
  draft: "bg-panel-2 text-ink-2",
};

export default function Inventory() {
  const [tab, setTab] = useState<"bestand" | "bewegungen" | "lager">("bestand");
  const [search, setSearch] = useState("");
  const [selectedSku, setSelectedSku] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["inventory-stock", search],
    queryFn: () => listStock({ search: search || undefined, limit: 200 }),
    staleTime: 30_000,
  });

  const items = data?.items ?? [];

  const stats = useMemo(() => {
    const totalUnits = items.reduce((s, i) => s + i.quantity_total, 0);
    const totalValue = items.reduce(
      (s, i) => s + Number(i.price) * i.quantity_total,
      0,
    );
    const critical = items.filter((i) => !i.ausverkauf && classifyStock(i).kind === "crit").length;
    const low = items.filter((i) => !i.ausverkauf && classifyStock(i).kind === "low").length;
    return { totalUnits, totalValue, critical, low };
  }, [items]);

  const byRisk = useMemo(
    () =>
      [...items]
        .filter((i) => i.is_active && !i.ausverkauf)
        .sort((a, b) => {
          const ra = a.quantity_alert
            ? a.quantity_total / Math.max(a.quantity_alert, 1)
            : a.quantity_total / 100;
          const rb = b.quantity_alert
            ? b.quantity_total / Math.max(b.quantity_alert, 1)
            : b.quantity_total / 100;
          return ra - rb;
        })
        .slice(0, 10),
    [items],
  );

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl text-ink">Lagerbestand</h1>
          <p className="text-sm text-ink-2 mt-1">
            Echtzeit-Übersicht über Bestände, Bewegungen und Nachbestellungen
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" disabled>
            <ArrowDownToLine className="size-3.5" />
            Wareneingang
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" disabled>
            <ArrowUpFromLine className="size-3.5" />
            Kommissionieren
          </Button>
          <Button size="sm" className="gap-1.5" disabled>
            <Plus className="size-3.5" />
            Nachbestellung
          </Button>
        </div>
      </header>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-md border border-line bg-panel p-4">
          <div className="text-xs text-ink-2 uppercase tracking-wide">
            Lagerbestand gesamt
          </div>
          <div className="font-display font-bold text-2xl text-ink mt-1">
            {stats.totalUnits.toLocaleString("de-DE")}
          </div>
          <div className="text-[11px] text-muted mt-2">
            Einheiten · {items.length} SKUs
          </div>
        </div>
        <div className="rounded-md border border-line bg-panel p-4">
          <div className="text-xs text-ink-2 uppercase tracking-wide">Warenwert</div>
          <div className="font-display font-bold text-2xl text-ink mt-1">
            {fmtEUR(stats.totalValue)}
          </div>
          <div className="text-[11px] text-muted mt-2">auf Basis Verkaufspreise</div>
        </div>
        <div className="rounded-md border border-line bg-panel p-4">
          <div className="text-xs text-ink-2 uppercase tracking-wide">
            Kritische Bestände
          </div>
          <div
            className="font-display font-bold text-2xl mt-1"
            style={{ color: "var(--coral)" }}
          >
            {stats.critical}
          </div>
          <div className="text-[11px] text-muted mt-2">Nachbestellung fällig</div>
        </div>
        <div className="rounded-md border border-line bg-panel p-4">
          <div className="text-xs text-ink-2 uppercase tracking-wide">
            Niedrige Bestände
          </div>
          <div
            className="font-display font-bold text-2xl mt-1"
            style={{ color: "var(--warn)" }}
          >
            {stats.low}
          </div>
          <div className="text-[11px] text-muted mt-2">unter Sicherheitsbestand</div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          <TabsTrigger value="bestand">Bestandsliste</TabsTrigger>
          <TabsTrigger value="bewegungen">Bewegungen</TabsTrigger>
          <TabsTrigger value="lager">Lagerorte</TabsTrigger>
        </TabsList>

        <TabsContent value="bestand" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Panel
              title="Füllstand nach Risiko"
              subtitle="Geringste Reichweite zuerst"
              className="lg:col-span-1"
            >
              {byRisk.length === 0 ? (
                <p className="text-sm text-ink-2">Keine aktiven Artikel.</p>
              ) : (
                <ul className="space-y-3">
                  {byRisk.map((p) => {
                    const cap = Math.max(p.quantity_alert ?? 50, 50) * 4;
                    const pct = Math.max(
                      3,
                      Math.min(100, Math.round((p.quantity_total / cap) * 100)),
                    );
                    const bucket = classifyStock(p);
                    const barColor =
                      bucket.kind === "crit"
                        ? "bg-coral"
                        : bucket.kind === "low"
                          ? "bg-warn"
                          : "bg-success";
                    return (
                      <li key={p.sku}>
                        <div className="flex items-baseline gap-2 text-sm mb-1">
                          <span className="font-medium truncate flex-1">{p.name}</span>
                          <span className="font-mono text-[11px] text-muted">
                            {p.sku}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-panel-2 rounded">
                            <div
                              className={`h-full rounded ${barColor}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="font-mono text-xs text-ink-2 w-12 text-right">
                            {p.quantity_total}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Panel>

            <Panel
              title="Bestandstabelle"
              subtitle={`${items.length} Artikel`}
              className="lg:col-span-2"
              actions={
                <Input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="SKU, Name, Hersteller…"
                  className="h-8 text-xs w-56"
                />
              }
              bodyClassName="p-0"
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line-2 text-xs uppercase text-muted">
                      <th className="text-left font-medium px-3 py-2">SKU</th>
                      <th className="text-left font-medium px-2 py-2">Produkt</th>
                      <th className="text-right font-medium px-2 py-2">Bestand</th>
                      <th className="text-right font-medium px-2 py-2">Lokal</th>
                      <th className="text-right font-medium px-2 py-2">eBay</th>
                      <th className="text-right font-medium px-2 py-2">Reserviert</th>
                      <th className="text-right font-medium px-2 py-2">Alert ab</th>
                      <th className="text-left font-medium px-2 py-2">Status</th>
                      <th className="text-left font-medium px-2 py-2 w-[180px]">
                        Anpassen
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan={9} className="text-center py-8 text-ink-2">
                          Lade…
                        </td>
                      </tr>
                    ) : items.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-8 text-ink-2">
                          Keine Artikel.
                        </td>
                      </tr>
                    ) : (
                      items.map((item) => {
                        const bucket = classifyStock(item);
                        const isSelected = selectedSku === item.sku;
                        return (
                          <tr
                            key={item.sku}
                            className={`border-b border-line-2 last:border-0 cursor-pointer ${
                              isSelected ? "bg-primary-soft" : "hover:bg-panel-2"
                            }`}
                            onClick={() => setSelectedSku(item.sku)}
                          >
                            <td className="px-3 py-2 font-mono text-xs text-ink-2">
                              {item.sku}
                            </td>
                            <td className="px-2 py-2 truncate max-w-[260px]">
                              {item.name}
                            </td>
                            <td className="px-2 py-2 text-right font-mono font-semibold">
                              {item.quantity_total.toLocaleString("de-DE")}
                            </td>
                            <td className="px-2 py-2 text-right font-mono text-ink-2">
                              {item.quantity_available.toLocaleString("de-DE")}
                            </td>
                            <td className="px-2 py-2 text-right font-mono text-ink-2">
                              {item.quantity_ebay > 0
                                ? item.quantity_ebay.toLocaleString("de-DE")
                                : "—"}
                            </td>
                            <td className="px-2 py-2 text-right font-mono text-ink-2">
                              {item.quantity_reserved.toLocaleString("de-DE")}
                            </td>
                            <td className="px-2 py-2 text-right font-mono text-muted">
                              {item.quantity_alert ?? "—"}
                            </td>
                            <td className="px-2 py-2">
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_PILL[bucket.kind]}`}
                              >
                                <span className="inline-block size-1.5 rounded-full bg-current" />
                                {bucket.label}
                              </span>
                            </td>
                            <td
                              className="px-2 py-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <StockLevelEditor item={item} />
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </TabsContent>

        <TabsContent value="bewegungen">
          {selectedSku ? (
            <MovementHistory sku={selectedSku} />
          ) : (
            <Panel title="Bewegungen" subtitle="Verlauf nach SKU">
              <p className="text-sm text-ink-2">
                Wähle in der Bestandsliste einen Artikel aus, um die Bestandsbewegungen
                anzuzeigen. Eine globale Bewegungs-Übersicht erfordert einen Backend-Endpoint
                und folgt in einem späteren Schritt.
              </p>
            </Panel>
          )}
        </TabsContent>

        <TabsContent value="lager">
          <LocationOverview />
        </TabsContent>
      </Tabs>
    </div>
  );
}
